import { collection } from '@/infra/collection'
import { Document } from '@/features/docs/Document'
import DataLoader from 'dataloader'
import { type DocsApi } from '@/features/docs/api/docs-api'
import { NotFound } from 'fejl'
import {
  mapDocumentAccessResourceToProps,
  mapDocumentFileResourceToDocumentFile,
  mapDocumentResourceToProps,
} from '@/features/docs/document-mappers'
import { Id } from '@/infra/id'
import { type CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { type DocumentType } from '@/features/docs/DocumentType'
import { BehaviorSubject, mergeMap, type Observable, Subject } from 'rxjs'
import { triggerDownload } from '@/features/docs/document-download-util'
import { tapAction } from '@/utils/mobx-util'
import { type DocsRealtimeMessage } from '@/features/docs/api/resources/docs-realtime'
import type { DocumentAccessPayload } from '@/features/docs/api/resources/doc-accesses'
import type { DocumentAccess } from '@/features/docs/DocumentAccess'
import type { GetDocumentDownloadDelegate } from '@/features/shell/platform/types'
import { withResolvers } from '@/utils/promise-util'

/**
 * Manages documents.
 */
export class DocumentStore {
  private readonly _documentUploadQueue$ = new Subject<QueuedDocumentUpload>()
  private readonly _documentAdded$ = new Subject<Document>()
  private readonly _documentAccessAdded$ = new Subject<
    [Document, DocumentAccess]
  >()
  private readonly _documentAccessRemoved$ = new Subject<
    [Document, DocumentAccess]
  >()
  private readonly _docLoader = new DataLoader(this._fetchDocsCore.bind(this))

  readonly docs = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new Document(this._getType),
  })

  /**
   * Emits when a document is added.
   */
  readonly documentAdded$ = this._documentAdded$.asObservable()

  /**
   * Emits when a document access is added.
   */
  readonly documentAccessAdded$ = this._documentAccessAdded$.asObservable()

  /**
   * Emits when a document access is removed.
   */
  readonly documentAccessRemoved$ = this._documentAccessRemoved$.asObservable()

  /**
   * Emits when a document is removed.
   */
  readonly documentRemoved$ = this.docs.removals$

  constructor(
    private readonly _token$: Observable<string>,
    private readonly _api: DocsApi,
    private readonly _getType: (id: Id) => DocumentType | null,
    private readonly _getDocumentDownloadDelegate: GetDocumentDownloadDelegate,
  ) {
    this._api.documents
      .events()
      .pipe(tapAction((evt) => this._handleRealtimeEvent(evt)))
      .subscribe()

    this._documentUploadQueue$
      .pipe(
        mergeMap(
          (queued) => this._upload(queued),
          // Allow up to 3 concurrent uploads
          3,
        ),
      )
      .subscribe()
  }

  /**
   * Lists documents.
   *
   * @param query
   * @param signal
   */
  async listDocuments(
    query: ListDocumentsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Document>> {
    const response = await this._api.documents.list(
      {
        after: query.after,
        limit: query.limit,
        archived: false,
        accesses: true,
        document_types: query.types?.map(Id.assert),
        ...(query.context.type === 'Job'
          ? {
              context: 'Job',
              job_id: Id.assert(query.context.jobId),
            }
          : {
              context: 'User',
              workspace_id: query.context.workspaceId,
            }),
      },
      signal,
    )

    // We'll clear the cursor if we got fewer results back than what we asked
    // for. We can do this because we know that doing another query
    // will return nothing.
    const next = response.data.length < query.limit ? null : response.cursor
    return {
      next,
      total: response.total,
      data: response.data.map((r) =>
        this.docs.set(mapDocumentResourceToProps(r)),
      ),
    }
  }

  /**
   * Fetches the document by ID.
   *
   * @param id
   */
  async fetchDocument(id: Id) {
    return await this._docLoader.load(Id.assert(id))
  }

  /**
   * Renames the document.
   */
  async rename(document: Document, newName: string) {
    const response = await this._api.documents.patch(Id.assert(document.id), {
      title: newName,
    })
    const props = mapDocumentResourceToProps(response)
    this.docs.set(props)
  }

  /**
   * Archives the document.
   */
  async archive(document: Document) {
    await this._api.documents.archive(Id.assert(document.id))

    this.docs.remove(document)
  }

  /**
   * Downloads the document.
   */
  download({ document, preview }: DownloadDocumentOptions) {
    // In order for this to work, it must be done in a user-initiated event.
    // Therefore, the token must be available synchronously.
    let token: string | null = null
    this._token$.subscribe((t) => (token = t)).unsubscribe()

    if (!token) {
      throw new Error('Unable to download document as there is no token')
    }

    const downloadDelegate = this._getDocumentDownloadDelegate()
    if (downloadDelegate.requiresFormSubmission) {
      triggerDownload(document, preview ?? false, token)
      return
    }

    // Otherwise get a signed download URL
    void this._api.documents
      .getSignedDownloadUrl(Id.assert(document.id), preview)
      .then((r) => downloadDelegate.download(r.url))
  }

  /**
   * Uploads a new document.
   */
  upload(options: UploadDocumentOptions): UploadDocumentObserver {
    const createdResolvers = withResolvers<Document>()
    const completedResolvers = withResolvers<Document>()
    const progress$ = new BehaviorSubject<UploadDocumentProgress>({
      total: options.file.size,
      uploaded: 0,
    })

    const queued: QueuedDocumentUpload = {
      createdResolvers,
      completedResolvers,
      progress$,
      options,
    }
    this._documentUploadQueue$.next(queued)

    return {
      created: createdResolvers.promise,
      completed: completedResolvers.promise,
      progress$,
    }
  }

  /**
   * Uploads a document.
   *
   * This method won't throw for any document upload related reasons, those errors
   * will be propagated to the caller that queued the upload.
   *
   * @param queued Queued document upload.
   * @returns
   */
  private async _upload(queued: QueuedDocumentUpload) {
    const { progress$, createdResolvers, completedResolvers } = queued
    const { file, workspaceId, title, documentTypeId, access, signal } =
      queued.options

    let doc: Document
    try {
      signal?.throwIfAborted()

      const accessPayload: DocumentAccessPayload | undefined =
        access?.type === 'Job'
          ? {
              type: 'Job',
              job_id: Id.assert(access.jobId),
            }
          : undefined

      const resource = await this._api.documents.create({
        access: accessPayload,
        title: title || file.name,
        document_type_id: documentTypeId,
        workspace_id: Id.assert(workspaceId),
        description: '',
      })

      const props = mapDocumentResourceToProps(resource)
      doc = this.docs.set(props)
      this._documentAdded$.next(doc)
      createdResolvers.resolve(doc)
    } catch (err: unknown) {
      createdResolvers.reject(err)
      completedResolvers.reject(err)
      progress$.complete()
      // Errors are handled by the consumer of the resolvers, no need to throw
      return
    }

    try {
      const id = Id.assert(doc.id)

      // Once the document is created, we can start uploading the file.
      const resource = await this._api.versions.create(
        {
          document_id: id,
          message: 'Initial version',
          action: {
            type: 'ReplaceContents',
            files: [file],
          },
        },
        {
          signal,
          // Track upload progress
          onProgress: (uploaded, total) => progress$.next({ uploaded, total }),
        },
      )

      // Make sure we emit the last progress value before completing.
      progress$.next({
        uploaded: progress$.value.total,
        total: progress$.value.total,
      })

      progress$.complete()

      doc.set({
        latestVersionId: resource.id,
        file: mapDocumentFileResourceToDocumentFile(resource.files[0]),
      })

      completedResolvers.resolve(doc)
    } catch (err: unknown) {
      // Errors are handled by the consumer of the resolvers, no need to throw
      progress$.complete()
      completedResolvers.reject(err)
    }
  }

  /**
   * Calls the API to get the documents.
   */
  private async _fetchDocsCore(
    ids: readonly string[],
  ): Promise<(Document | null)[]> {
    const resources = await Promise.all(
      ids.map((id) =>
        this._api.documents
          .get(id, { accesses: true })
          .catch(NotFound.ignore(null)),
      ),
    )

    return this.docs.set(
      resources.map((r) => (r ? mapDocumentResourceToProps(r) : null)),
    )
  }

  /**
   * Creates a new document access.
   *
   * @param documentId ID of the document to create access for
   * @param access Access details to create
   */
  async createDocumentAccess(documentId: Id, access: NewDocumentAccessOptions) {
    const id = Id.assert(documentId)

    const payload: DocumentAccessPayload = {
      type: 'Job',
      job_id: Id.assert(access.jobId),
    }

    const result = await this._api.accesses.create({
      document_id: id,
      access: payload,
    })

    // Update the document's accesses directly if it exists in the collection
    const document = this.docs.get(id)
    if (document) {
      // Map the resource to DocumentAccessProps and add to the collection
      const accessProps = mapDocumentAccessResourceToProps(result)
      const access = document.accesses.set(accessProps)
      this._documentAccessAdded$.next([document, access])
    }
  }

  /**
   * Removes a document access.
   *
   * @param documentId ID of the document this access belongs to
   * @param accessId ID of the access to remove
   */
  async removeDocumentAccess(documentId: Id, accessId: Id) {
    const id = Id.assert(accessId)
    await this._api.accesses.remove(id)

    // Update the document's accesses directly if it exists in the collection
    const document = this.docs.get(documentId)
    if (document) {
      const accessToRemove = document.accesses.get(Id.assert(id))
      if (accessToRemove) {
        document.accesses.remove(accessToRemove)
        this._documentAccessRemoved$.next([document, accessToRemove])
      }
    }
  }

  /**
   * Handles a realtime event.
   *
   * @param evt
   * @private
   */
  private _handleRealtimeEvent(evt: DocsRealtimeMessage) {
    if (evt.type === 'VersionScanned' || evt.type === 'ClientDataVerified') {
      if (evt.data.files.length === 0) {
        return
      }

      const file = mapDocumentFileResourceToDocumentFile(evt.data.files[0])
      const documentId = Id.assert(evt.data.document_id)

      if (!this.docs.get(documentId)) {
        void this.fetchDocument(documentId).then((doc) => {
          if (doc) {
            this._documentAdded$.next(doc)
          }
        })
        return
      }

      this.docs.set({
        id: evt.data.document_id,
        file,
        latestVersionId: evt.data.id,
      })
    }

    if (evt.type === 'AccessCreated') {
      const accessProps = mapDocumentAccessResourceToProps(evt.data)
      const document = this.docs.get(evt.data.document_id)
      if (document) {
        const alreadySeen = !!document.accesses.get(accessProps.id)
        const access = document.accesses.set(accessProps)
        if (!alreadySeen) {
          this._documentAccessAdded$.next([document, access])
        }
      } else {
        // Fetch the document and emit
        void this.fetchDocument(accessProps.documentId).then((doc) => {
          const access = doc?.accesses.get(accessProps.id)
          if (doc && access) {
            this._documentAccessAdded$.next([doc, access])
          }
        })
      }
    }

    if (evt.type === 'AccessUpdated') {
      const accessProps = mapDocumentAccessResourceToProps(evt.data)
      const document = this.docs.get(evt.data.document_id)
      if (document) {
        document.accesses.set(accessProps)
      }
    }

    if (evt.type === 'AccessDeleted') {
      const accessProps = mapDocumentAccessResourceToProps(evt.data)
      const document = this.docs.get(evt.data.document_id)
      if (document) {
        // We've "already seen" the removal if the current doc doesn't
        // have the access.
        const accessBeingRemoved = document.accesses.get(accessProps.id)
        document.accesses.remove(Id.assert(accessProps.id))
        if (accessBeingRemoved) {
          this._documentAccessRemoved$.next([document, accessBeingRemoved])
        }
      }
    }

    if (evt.type !== 'DocumentCreated' && evt.type !== 'DocumentUpdated') {
      return
    }

    if (evt.data.latest_version?.files.some((f) => f.state === 'Pending')) {
      return
    }

    if (evt.data.archived) {
      this.docs.remove(evt.data.id)
    } else {
      const alreadySeen = !!this.docs.get(evt.data.id)
      const doc = this.docs.set(mapDocumentResourceToProps(evt.data))
      if (!alreadySeen) {
        this._documentAdded$.next(doc)
      }
    }
  }
}

/**
 * Options for downloading a document.
 */
export interface DownloadDocumentOptions {
  document: Document
  preview?: boolean
}

/**
 * Query for listing documents with pagination.
 */
export interface ListDocumentsQuery extends CursorPageQuery {
  context: ListDocumentsQueryContext
  types?: Id[]
}

/**
 * Context for listing documents.
 */
export type ListDocumentsQueryContext =
  | { type: 'User'; workspaceId: Id }
  | { type: 'Job'; jobId: Id }

/**
 * Options for creating a new document access.
 */
export interface NewDocumentAccessOptions {
  type: 'Job'
  jobId: Id
}

/**
 * Options for {@link DocumentStore.upload}.
 */
export interface UploadDocumentOptions {
  /**
   * Workspace ID in which to upload the document.
   */
  workspaceId: Id

  /**
   * Title of the document. Will be set to the file name if not set.
   */
  title?: string

  /**
   * The file to upload.
   */
  file: File

  /**
   * Document type to assign to the new document, if any.
   */
  documentTypeId?: Id

  /**
   * Optional access to create for the new document.
   */
  access?: NewDocumentAccessOptions | null

  /**
   * Optional abort signal.
   */
  signal?: AbortSignal
}

/**
 * Returned by {@link DocumentStore.upload} to
 * observe the progress of the upload, as well as retrieved the
 * created document on completion.
 */
export interface UploadDocumentObserver {
  /**
   * A promise that completes when the document has been created, but before
   * it has been uploaded.
   * The promise will reject if creation fails.
   */
  created: Promise<Document>

  /**
   * A promise that completes when the upload has been completed.
   * The promise will reject if uploading fails.
   */
  completed: Promise<Document>

  /**
   * An observable that emits upload progress information.
   * Will emit the current progress immediately on subscribe,
   * and completes when the upload is completed.
   */
  progress$: Observable<UploadDocumentProgress>
}

/**
 * Emitted by the {@link Observable} returned
 * from {@link DocumentStore.upload} to observe the progress of the upload.
 */
export interface UploadDocumentProgress {
  /**
   * How much has been uploaded so far in bytes.
   */
  uploaded: number

  /**
   * Total amount needed to upload.
   */
  total: number
}

type Resolvers<T> = ReturnType<typeof withResolvers<T>>

interface QueuedDocumentUpload {
  options: UploadDocumentOptions
  progress$: BehaviorSubject<UploadDocumentProgress>
  createdResolvers: Resolvers<Document>
  completedResolvers: Resolvers<Document>
}
